<script setup lang="ts">
import { nextTick } from "vue";
import Modal from "@/ui/components/modal.vue";
import { PlanIssue, useBasePriceStore } from "@/modules/base_price/store";

const store = useBasePriceStore();
const visible = defineModel<boolean>("visible", { default: false });

/**
 * Takes you to the thing that's wrong. The tab is set after the selection
 * settles: selecting a book resets the open tab to Budget split (see
 * PlanNodeDetailPanel), and that watcher runs before this would, so setting it
 * in the same tick is silently undone.
 */
const goTo = async (issue: PlanIssue) => {
	if (issue.bookUuid) store.selectBook(issue.bookUuid);

	if (issue.assetId !== null) {
		store.selectAsset(issue.nodeId, issue.assetId);
	} else {
		store.selectNode(issue.nodeId);
	}

	visible.value = false;

	await nextTick();
	store.activeDetailTab = issue.tab;
};
</script>

<template>
	<modal
		v-model:visible="visible"
		title="Plan issues"
		icon="fa-light fa-triangle-exclamation"
		width="52rem"
		:persistent="false"
		hide-actions
	>
		<div v-if="!store.planIssues.length" class="text-body-2 text-medium-emphasis">
			Nothing to fix — every level's budget adds up, every pinned month fits,
			and no price contradicts its bounds.
		</div>

		<div v-else class="d-flex flex-column ga-3">
			<div class="text-body-2 text-medium-emphasis" style="max-width: 640px">
				Each of these is a configuration that disagrees with itself, so the
				resolver had to choose. Revenue landing off budget is not listed — a
				hand-set price or a price bound is meant to cause that.
			</div>

			<v-card
				v-for="issue in store.planIssues"
				:key="issue.id"
				variant="outlined"
				class="pa-4 issue-card"
				@click="goTo(issue)"
			>
				<div class="d-flex align-start ga-3">
					<i class="fa-light fa-triangle-exclamation issue-icon mt-1" />
					<div class="flex-grow-1" style="min-width: 0">
						<div class="text-body-2 font-weight-bold">{{ issue.title }}</div>
						<div class="text-caption text-medium-emphasis mt-1">
							{{ issue.detail }}
						</div>
						<div class="text-caption text-medium-emphasis mt-2">
							{{ issue.bookName }} · {{ issue.targetName }} ·
							{{ issue.tabLabel }}
						</div>
					</div>
					<i class="fa-light fa-arrow-right issue-go mt-1" />
				</div>
			</v-card>
		</div>
	</modal>
</template>

<style scoped lang="scss">
.issue-card {
	cursor: pointer;
}

.issue-card:hover {
	background: rgba(var(--v-theme-on-surface), 0.02);
}

.issue-icon {
	color: rgb(var(--v-theme-error));
	font-size: 14px;
	flex: 0 0 auto;
}

.issue-go {
	color: rgba(var(--v-theme-on-surface), 0.4);
	font-size: 12px;
	flex: 0 0 auto;
}
</style>
